/**
 * Ad Feature Management - Admin Panel
 * Complete redesign with unique class names to avoid theme conflicts
 */

import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  Users,
  Eye,
  MousePointer,
  TrendingUp,
  Settings,
  ChevronRight,
  RefreshCw,
  DollarSign,
  Calendar,
  Clock,
  Trash2,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Shield,
  Download,
  X,
  XCircle,
  CheckSquare,
  Square,
  Plus,
  Edit,
  Type,
  Image
} from 'lucide-react';
import './AdFeatureManagement.css';

const AdFeatureManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Data states
  const [config, setConfig] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [fraudStats, setFraudStats] = useState(null);
  const [fraudSettings, setFraudSettings] = useState(null);
  const [editingFraudSettings, setEditingFraudSettings] = useState(false);
  const [fraudSettingsForm, setFraudSettingsForm] = useState(null);
  const [savingFraudSettings, setSavingFraudSettings] = useState(false);
  const [fraudMerchants, setFraudMerchants] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [appealCounts, setAppealCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0, total: 0 });
  const [appealFilter, setAppealFilter] = useState('PENDING');
  const [processingAppeal, setProcessingAppeal] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // UI states
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [subFilter, setSubFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Modals
  const [configModal, setConfigModal] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: null, data: null });

  // Grant form
  const [grantForm, setGrantForm] = useState({ merchantId: '', plan: 'MONTHLY', durationMonths: 1 });
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantResults, setMerchantResults] = useState([]);

  // Config form
  const [configForm, setConfigForm] = useState({
    isEnabled: true,
    monthlyPriceNGN: 20000,
    yearlyPriceNGN: 192000,
    yearlyDiscount: 20
  });

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        await Promise.all([fetchConfig(), fetchAnalytics()]);
      } else if (activeTab === 'subscriptions') {
        await fetchSubscriptions();
      } else if (activeTab === 'campaigns') {
        await fetchCampaigns();
        setSelectedCampaigns([]);
      } else if (activeTab === 'analytics') {
        await fetchAnalytics();
      } else if (activeTab === 'fraud') {
        await Promise.all([fetchFraudAlerts(), fetchFraudStats(), fetchFraudSettings(), fetchFraudMerchants(), fetchAppeals()]);
      } else if (activeTab === 'scheduler') {
        await fetchSchedulerStatus();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await fetch('/api/admin/ads/config', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        if (data.config) {
          setConfigForm({
            isEnabled: data.config.isEnabled,
            monthlyPriceNGN: data.config.monthlyPriceNGN,
            yearlyPriceNGN: data.config.yearlyPriceNGN,
            yearlyDiscount: data.config.yearlyDiscount
          });
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const params = new URLSearchParams();
      if (subFilter !== 'all') params.append('status', subFilter.toUpperCase());
      const res = await fetch(`/api/admin/ads/subscriptions?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const params = new URLSearchParams();
      if (campaignFilter !== 'all') params.append('status', campaignFilter.toUpperCase());
      const res = await fetch(`/api/admin/ads/campaigns?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);
      const res = await fetch(`/api/admin/ads/analytics?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchFraudAlerts = async () => {
    try {
      const res = await fetch('/api/admin/ads/fraud/alerts', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFraudAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error fetching fraud alerts:', error);
    }
  };

  const fetchFraudStats = async () => {
    try {
      const res = await fetch('/api/admin/ads/fraud/stats', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFraudStats(data);
      }
    } catch (error) {
      console.error('Error fetching fraud stats:', error);
    }
  };

  const fetchFraudSettings = async () => {
    try {
      const res = await fetch('/api/admin/ads/fraud/settings', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFraudSettings(data.settings);
        setFraudSettingsForm(data.settings);
      }
    } catch (error) {
      console.error('Error fetching fraud settings:', error);
    }
  };

  const handleSaveFraudSettings = async () => {
    try {
      setSavingFraudSettings(true);
      const res = await fetch('/api/admin/ads/fraud/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fraudSettingsForm)
      });
      if (res.ok) {
        const data = await res.json();
        setFraudSettings(data.settings);
        setFraudSettingsForm(data.settings);
        setEditingFraudSettings(false);
        alert('Fraud settings saved successfully');
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to save fraud settings');
      }
    } catch (error) {
      console.error('Error saving fraud settings:', error);
      alert('Failed to save fraud settings');
    } finally {
      setSavingFraudSettings(false);
    }
  };

  const handleCancelEditFraudSettings = () => {
    setFraudSettingsForm(fraudSettings);
    setEditingFraudSettings(false);
  };

  const fetchFraudMerchants = async () => {
    try {
      const res = await fetch('/api/admin/ads/fraud/merchants', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFraudMerchants(data.merchants || []);
      }
    } catch (error) {
      console.error('Error fetching fraud merchants:', error);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const res = await fetch('/api/admin/ads/scheduler/status', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const fetchAppeals = async (status = appealFilter) => {
    try {
      const url = status === 'all'
        ? '/api/admin/ads/appeals'
        : `/api/admin/ads/appeals?status=${status}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAppeals(data.appeals || []);
        setAppealCounts(data.counts || { PENDING: 0, APPROVED: 0, REJECTED: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching appeals:', error);
    }
  };

  const handleApproveAppeal = async (appealId) => {
    if (!window.confirm('Approve this appeal and unflag the merchant?')) return;

    setProcessingAppeal(appealId);
    try {
      const res = await fetch(`/api/admin/ads/appeals/${appealId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminNotes: 'Appeal approved by admin' })
      });
      if (res.ok) {
        alert('Appeal approved and merchant unflagged');
        fetchAppeals();
        fetchFraudMerchants();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to approve appeal');
      }
    } catch (error) {
      console.error('Error approving appeal:', error);
      alert('Failed to approve appeal');
    } finally {
      setProcessingAppeal(null);
    }
  };

  const handleRejectAppeal = async (appealId) => {
    const reason = window.prompt('Enter reason for rejection (optional):');
    if (reason === null) return; // User cancelled

    setProcessingAppeal(appealId);
    try {
      const res = await fetch(`/api/admin/ads/appeals/${appealId}/reject`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminNotes: reason || 'Appeal rejected by admin' })
      });
      if (res.ok) {
        alert('Appeal rejected');
        fetchAppeals();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to reject appeal');
      }
    } catch (error) {
      console.error('Error rejecting appeal:', error);
      alert('Failed to reject appeal');
    } finally {
      setProcessingAppeal(null);
    }
  };

  const searchMerchants = async (query) => {
    if (!query || query.length < 2) {
      setMerchantResults([]);
      return;
    }
    try {
      const res = await fetch(`/api/admin/users?search=${encodeURIComponent(query)}&role=merchant&limit=10`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMerchantResults(data.users || []);
      }
    } catch (error) {
      console.error('Error searching merchants:', error);
    }
  };

  // Actions
  const handleSaveConfig = async () => {
    setActionLoading('config');
    try {
      const res = await fetch('/api/admin/ads/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(configForm)
      });
      if (res.ok) {
        setConfigModal(false);
        showToast('success', 'Configuration saved');
        fetchConfig();
      } else {
        showToast('error', 'Failed to save configuration');
      }
    } catch (error) {
      showToast('error', 'Error saving configuration');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantSubscription = async () => {
    if (!grantForm.merchantId) {
      showToast('error', 'Please select a merchant');
      return;
    }
    setActionLoading('grant');
    try {
      const res = await fetch('/api/admin/ads/subscriptions/grant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(grantForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setGrantModal(false);
        setGrantForm({ merchantId: '', plan: 'MONTHLY', durationMonths: 1 });
        setMerchantSearch('');
        showToast('success', 'Subscription granted');
        fetchSubscriptions();
      } else {
        showToast('error', data.error || 'Failed to grant subscription');
      }
    } catch (error) {
      showToast('error', 'Error granting subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCampaignAction = async (campaignId, action) => {
    setActionLoading(campaignId);
    try {
      const endpoint = `/api/admin/ads/campaigns/${campaignId}`;
      const method = action === 'delete' ? 'DELETE' : 'PUT';
      const body = action !== 'delete' ? JSON.stringify({ status: action === 'pause' ? 'PAUSED' : 'ACTIVE' }) : undefined;

      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', `Campaign ${action}d`);
        fetchCampaigns();
      } else {
        showToast('error', data.error || `Failed to ${action} campaign`);
      }
    } catch (error) {
      showToast('error', `Error: ${error.message}`);
    } finally {
      setActionLoading(null);
      setConfirmModal({ isOpen: false, action: null, data: null });
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedCampaigns.length === 0) return;
    setActionLoading('bulk');
    try {
      const res = await fetch('/api/admin/ads/campaigns/bulk-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ campaignIds: selectedCampaigns, action })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', data.message);
        setSelectedCampaigns([]);
        fetchCampaigns();
      } else {
        showToast('error', data.error || 'Bulk action failed');
      }
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setActionLoading(null);
      setConfirmModal({ isOpen: false, action: null, data: null });
    }
  };

  const handleDismissFraudAlert = async (alertId) => {
    setActionLoading(alertId);
    try {
      const res = await fetch(`/api/admin/ads/fraud/${alertId}/dismiss`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        showToast('success', 'Alert dismissed');
        fetchFraudAlerts();
        fetchFraudStats();
      }
    } catch (error) {
      showToast('error', 'Failed to dismiss alert');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRunScheduler = async () => {
    setActionLoading('scheduler');
    try {
      const res = await fetch('/api/admin/ads/scheduler/run', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', `Processed: ${data.result.activatedCampaigns} activated, ${data.result.expiredCampaigns} expired`);
        fetchSchedulerStatus();
      }
    } catch (error) {
      showToast('error', 'Failed to run scheduler');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFlagMerchant = async (merchantId, action = 'flag', reason = '') => {
    setActionLoading(merchantId);
    try {
      const res = await fetch('/api/admin/ads/fraud/flag-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ merchantId, action, reason })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', action === 'unflag' ? 'Merchant unflagged' : 'Merchant flagged for fraud');
        fetchFraudMerchants();
      } else {
        showToast('error', data.error || 'Failed to update merchant');
      }
    } catch (error) {
      showToast('error', 'Failed to update merchant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendMerchant = async (merchantId, reason = '') => {
    setActionLoading(`suspend-${merchantId}`);
    try {
      const res = await fetch('/api/admin/ads/fraud/suspend-merchant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ merchantId, reason })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', 'Merchant ad access suspended');
        fetchFraudMerchants();
      } else {
        showToast('error', data.error || 'Failed to suspend merchant');
      }
    } catch (error) {
      showToast('error', 'Failed to suspend merchant');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLiftSuspension = async (merchantId) => {
    setActionLoading(`lift-${merchantId}`);
    try {
      const res = await fetch('/api/admin/ads/fraud/lift-suspension', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ merchantId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('success', 'Suspension lifted successfully');
        fetchFraudMerchants();
      } else {
        showToast('error', data.error || 'Failed to lift suspension');
      }
    } catch (error) {
      showToast('error', 'Failed to lift suspension');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async (type) => {
    setActionLoading(`export-${type}`);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('startDate', dateRange.start);
      if (dateRange.end) params.append('endDate', dateRange.end);

      const res = await fetch(`/api/admin/ads/export/${type}?${params}`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast('success', 'Export complete');
      }
    } catch (error) {
      showToast('error', 'Export failed');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleCampaignSelection = (id) => {
    setSelectedCampaigns(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAllCampaigns = () => {
    setSelectedCampaigns(prev => prev.length === campaigns.length ? [] : campaigns.map(c => c.id));
  };

  // Helpers
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
  };

  // Computed
  const activeSubscriptions = subscriptions.filter(s => s.status === 'ACTIVE').length;
  const totalRevenue = subscriptions.reduce((sum, s) => sum + parseFloat(s.priceAtPurchase || 0), 0);
  const activeCampaigns = campaigns.filter(c => c.status === 'ACTIVE').length;
  const totalViews = analytics?.performance?.totalViews || campaigns.reduce((sum, c) => sum + (c.totalViews || 0), 0);
  const totalClicks = analytics?.performance?.totalClicks || campaigns.reduce((sum, c) => sum + (c.totalClicks || 0), 0);

  // =========================================
  // RENDER TABS
  // =========================================

  const renderOverview = () => (
    <>
      {/* Feature Status */}
      <div className="afm-status-card">
        <div className="afm-status-left">
          <div className={`afm-status-badge ${config?.isEnabled ? 'enabled' : 'disabled'}`}>
            <CheckCircle size={16} />
            <span>{config?.isEnabled ? 'Feature Enabled' : 'Feature Disabled'}</span>
          </div>
          <div className="afm-status-pricing">
            <span>Monthly: {formatCurrency(config?.monthlyPriceNGN)}</span>
            <span>Yearly: {formatCurrency(config?.yearlyPriceNGN)} ({config?.yearlyDiscount}% off)</span>
          </div>
        </div>
        <button className="afm-btn afm-btn-outline" onClick={() => setConfigModal(true)}>
          <Settings size={14} /> Configure
        </button>
      </div>

      {/* Stats Grid */}
      <div className="afm-stats-grid">
        <div className="afm-stat-card">
          <div className="afm-stat-icon purple"><Users size={20} /></div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{activeSubscriptions}</span>
            <span className="afm-stat-label">Active Subscribers</span>
          </div>
        </div>
        <div className="afm-stat-card">
          <div className="afm-stat-icon green"><Megaphone size={20} /></div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{activeCampaigns}</span>
            <span className="afm-stat-label">Active Campaigns</span>
          </div>
        </div>
        <div className="afm-stat-card">
          <div className="afm-stat-icon blue"><Eye size={20} /></div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{formatNumber(totalViews)}</span>
            <span className="afm-stat-label">Total Views</span>
          </div>
        </div>
        <div className="afm-stat-card">
          <div className="afm-stat-icon teal"><MousePointer size={20} /></div>
          <div className="afm-stat-content">
            <span className="afm-stat-value">{formatNumber(totalClicks)}</span>
            <span className="afm-stat-label">Total Clicks</span>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="afm-overview-grid">
        <div className="afm-overview-card">
          <div className="afm-overview-header">
            <h3>Revenue Overview</h3>
            <button className="afm-link-btn" onClick={() => setActiveTab('subscriptions')}>
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="afm-overview-body">
            <div className="afm-revenue-item">
              <DollarSign size={24} className="afm-revenue-icon" />
              <div>
                <span className="afm-revenue-value">{formatCurrency(totalRevenue)}</span>
                <span className="afm-revenue-label">Total Revenue</span>
              </div>
            </div>
            <div className="afm-revenue-item">
              <TrendingUp size={24} className="afm-revenue-icon" />
              <div>
                <span className="afm-revenue-value">{subscriptions.length}</span>
                <span className="afm-revenue-label">All Subscriptions</span>
              </div>
            </div>
          </div>
        </div>

        <div className="afm-overview-card">
          <div className="afm-overview-header">
            <h3>Campaign Performance</h3>
            <button className="afm-link-btn" onClick={() => setActiveTab('analytics')}>
              View All <ChevronRight size={14} />
            </button>
          </div>
          <div className="afm-overview-body">
            <div className="afm-perf-grid">
              <div className="afm-perf-item">
                <span className="afm-perf-label">CTR</span>
                <span className="afm-perf-value">{totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(2) : '0'}%</span>
              </div>
              <div className="afm-perf-item">
                <span className="afm-perf-label">Total Campaigns</span>
                <span className="afm-perf-value">{campaigns.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderSubscriptions = () => (
    <>
      <div className="afm-section-header">
        <h2>Subscriptions</h2>
        <div className="afm-header-actions">
          <button className="afm-btn afm-btn-outline" onClick={() => handleExport('subscriptions')} disabled={actionLoading === 'export-subscriptions'}>
            <Download size={14} /> {actionLoading === 'export-subscriptions' ? 'Exporting...' : 'Export'}
          </button>
          <button className="afm-btn afm-btn-primary" onClick={() => setGrantModal(true)}>
            <Plus size={14} /> Grant Subscription
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="afm-filters">
        {['all', 'active', 'expired', 'cancelled'].map(filter => (
          <button
            key={filter}
            className={`afm-filter-pill ${subFilter === filter ? 'active' : ''}`}
            onClick={() => { setSubFilter(filter); setTimeout(fetchSubscriptions, 100); }}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="afm-table-container">
        {subscriptions.length === 0 ? (
          <div className="afm-empty">
            <Users size={48} />
            <h3>No Subscriptions</h3>
            <p>No subscriptions match your filter</p>
          </div>
        ) : (
          <table className="afm-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map(sub => (
                <tr key={sub.id}>
                  <td>
                    <div className="afm-cell-main">{sub.merchant?.store?.name || sub.merchant?.email}</div>
                    <div className="afm-cell-sub">{sub.merchant?.email}</div>
                  </td>
                  <td><span className={`afm-badge plan-${sub.plan?.toLowerCase()}`}>{sub.plan}</span></td>
                  <td><span className={`afm-badge status-${sub.status?.toLowerCase()}`}>{sub.status}</span></td>
                  <td>{formatDate(sub.startDate)}</td>
                  <td>{formatDate(sub.endDate)}</td>
                  <td>{formatCurrency(sub.priceAtPurchase)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  const renderCampaigns = () => (
    <>
      <div className="afm-section-header">
        <h2>All Campaigns</h2>
        <div className="afm-header-actions">
          <button className="afm-btn afm-btn-outline" onClick={() => handleExport('campaigns')} disabled={actionLoading === 'export-campaigns'}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedCampaigns.length > 0 && (
        <div className="afm-bulk-bar">
          <span className="afm-bulk-count">{selectedCampaigns.length} selected</span>
          <div className="afm-bulk-actions">
            <button className="afm-bulk-btn" onClick={() => setConfirmModal({ isOpen: true, action: 'bulk-pause', data: null })}>
              <Pause size={14} /> Pause
            </button>
            <button className="afm-bulk-btn success" onClick={() => setConfirmModal({ isOpen: true, action: 'bulk-resume', data: null })}>
              <Play size={14} /> Resume
            </button>
            <button className="afm-bulk-btn danger" onClick={() => setConfirmModal({ isOpen: true, action: 'bulk-delete', data: null })}>
              <Trash2 size={14} /> Delete
            </button>
            <button className="afm-bulk-btn cancel" onClick={() => setSelectedCampaigns([])}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="afm-filters">
        {['all', 'active', 'scheduled', 'draft', 'paused', 'expired'].map(filter => (
          <button
            key={filter}
            className={`afm-filter-pill ${campaignFilter === filter ? 'active' : ''}`}
            onClick={() => { setCampaignFilter(filter); setTimeout(fetchCampaigns, 100); }}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="afm-table-container">
        {campaigns.length === 0 ? (
          <div className="afm-empty">
            <Megaphone size={48} />
            <h3>No Campaigns</h3>
            <p>No campaigns match your filter</p>
          </div>
        ) : (
          <table className="afm-table">
            <thead>
              <tr>
                <th className="afm-checkbox-col">
                  <button className="afm-checkbox-btn" onClick={toggleAllCampaigns}>
                    {selectedCampaigns.length === campaigns.length ? <CheckSquare size={16} /> : <Square size={16} />}
                  </button>
                </th>
                <th>Campaign</th>
                <th>Merchant</th>
                <th>Client</th>
                <th>Type</th>
                <th>Status</th>
                <th>Views</th>
                <th>Clicks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(campaign => (
                <tr key={campaign.id} className={selectedCampaigns.includes(campaign.id) ? 'selected' : ''}>
                  <td className="afm-checkbox-col">
                    <button className="afm-checkbox-btn" onClick={() => toggleCampaignSelection(campaign.id)}>
                      {selectedCampaigns.includes(campaign.id) ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                  </td>
                  <td>
                    <div className="afm-cell-main">{campaign.headline || campaign.name || 'Untitled'}</div>
                    <div className="afm-cell-sub">{formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}</div>
                  </td>
                  <td>{campaign.merchant?.store?.name || campaign.merchant?.email || 'N/A'}</td>
                  <td>{campaign.client?.companyName || 'N/A'}</td>
                  <td>
                    <span className={`afm-badge type-${campaign.type?.toLowerCase()}`}>
                      {campaign.type === 'TEXT' ? <Type size={12} /> : <Image size={12} />}
                      {campaign.type}
                    </span>
                  </td>
                  <td><span className={`afm-badge status-${campaign.status?.toLowerCase()}`}>{campaign.status}</span></td>
                  <td>{formatNumber(campaign.totalViews || 0)}</td>
                  <td>{formatNumber(campaign.totalClicks || 0)}</td>
                  <td>
                    <div className="afm-action-btns">
                      {campaign.status === 'ACTIVE' && (
                        <button className="afm-icon-btn" onClick={() => setConfirmModal({ isOpen: true, action: 'pause', data: campaign })} title="Pause">
                          <Pause size={14} />
                        </button>
                      )}
                      {(campaign.status === 'PAUSED' || campaign.status === 'DRAFT') && (
                        <button className="afm-icon-btn success" onClick={() => handleCampaignAction(campaign.id, 'resume')} title="Resume">
                          <Play size={14} />
                        </button>
                      )}
                      <button className="afm-icon-btn danger" onClick={() => setConfirmModal({ isOpen: true, action: 'delete', data: campaign })} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  const renderAnalytics = () => (
    <>
      <div className="afm-section-header">
        <h2>Platform Analytics</h2>
        <div className="afm-header-actions">
          <div className="afm-date-filter">
            <input type="date" value={dateRange.start} onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} />
            <span>to</span>
            <input type="date" value={dateRange.end} onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} />
            <button className="afm-btn afm-btn-primary" onClick={fetchAnalytics}>Apply</button>
          </div>
          <button className="afm-btn afm-btn-outline" onClick={() => handleExport('analytics')}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="afm-summary-grid">
        <div className="afm-summary-card">
          <span className="afm-summary-label">Total Views</span>
          <span className="afm-summary-value">{formatNumber(analytics?.performance?.totalViews || 0)}</span>
        </div>
        <div className="afm-summary-card">
          <span className="afm-summary-label">Total Clicks</span>
          <span className="afm-summary-value">{formatNumber(analytics?.performance?.totalClicks || 0)}</span>
        </div>
        <div className="afm-summary-card">
          <span className="afm-summary-label">Avg CTR</span>
          <span className="afm-summary-value">{analytics?.performance?.overallCTR || '0'}%</span>
        </div>
        <div className="afm-summary-card">
          <span className="afm-summary-label">Active Campaigns</span>
          <span className="afm-summary-value">{analytics?.campaigns?.byStatus?.ACTIVE || activeCampaigns}</span>
        </div>
      </div>

      {/* Top Merchants */}
      {analytics?.topMerchants?.length > 0 && (
        <div className="afm-card">
          <h3>Top Performing Merchants</h3>
          <table className="afm-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Store</th>
                <th>Views</th>
                <th>Clicks</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {analytics.topMerchants.slice(0, 10).map((m, idx) => (
                <tr key={idx}>
                  <td>{m.merchant?.email || 'Unknown'}</td>
                  <td>{m.merchant?.store?.name || 'N/A'}</td>
                  <td>{formatNumber(m.views)}</td>
                  <td>{formatNumber(m.clicks)}</td>
                  <td>{m.ctr}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const renderFraud = () => (
    <>
      <div className="afm-section-header">
        <h2>Fraud Prevention & Monitoring</h2>
        <div className="afm-header-actions">
          <button className="afm-btn afm-btn-outline" onClick={() => handleExport('fraud')}>
            <Download size={14} /> Export
          </button>
          <button className="afm-btn afm-btn-outline" onClick={() => { fetchFraudAlerts(); fetchFraudStats(); fetchFraudMerchants(); fetchAppeals(); }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      {fraudStats && (
        <div className="afm-stats-grid">
          <div className="afm-stat-card">
            <div className="afm-stat-icon red"><Shield size={20} /></div>
            <div className="afm-stat-content">
              <span className="afm-stat-value">{fraudStats.activeAlerts || 0}</span>
              <span className="afm-stat-label">Active Alerts</span>
            </div>
          </div>
          <div className="afm-stat-card">
            <div className="afm-stat-icon orange"><AlertCircle size={20} /></div>
            <div className="afm-stat-content">
              <span className="afm-stat-value">{fraudStats.flaggedIPs || 0}</span>
              <span className="afm-stat-label">Flagged IPs</span>
            </div>
          </div>
          <div className="afm-stat-card">
            <div className="afm-stat-icon blue"><Eye size={20} /></div>
            <div className="afm-stat-content">
              <span className="afm-stat-value">{formatNumber(fraudStats.blockedClicks || 0)}</span>
              <span className="afm-stat-label">Blocked Clicks</span>
            </div>
          </div>
          <div className="afm-stat-card">
            <div className="afm-stat-icon green"><CheckCircle size={20} /></div>
            <div className="afm-stat-content">
              <span className="afm-stat-value">{fraudStats.dismissedAlerts || 0}</span>
              <span className="afm-stat-label">Dismissed</span>
            </div>
          </div>
        </div>
      )}

      {/* Platform Fraud Settings */}
      <div className="afm-card">
        <div className="afm-card-header-flex">
          <h3>Platform Fraud Settings</h3>
          {fraudSettings && !editingFraudSettings && (
            <button className="afm-btn-secondary" onClick={() => setEditingFraudSettings(true)}>
              <Edit size={14} /> Edit Settings
            </button>
          )}
        </div>
        {fraudSettings && fraudSettingsForm ? (
          editingFraudSettings ? (
            <div className="afm-fraud-settings-form">
              <div className="afm-form-section">
                <h4>Master Toggle</h4>
                <div className="afm-form-row">
                  <label className="afm-toggle-label">
                    <input
                      type="checkbox"
                      checked={fraudSettingsForm.isEnabled}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, isEnabled: e.target.checked }))}
                    />
                    <span>Fraud Protection Enabled</span>
                  </label>
                </div>
              </div>

              <div className="afm-form-section">
                <h4>Click Rate Limits</h4>
                <div className="afm-form-grid">
                  <div className="afm-form-group">
                    <label>Clicks per IP per Hour</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={fraudSettingsForm.clicksPerHourLimit}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, clicksPerHourLimit: parseInt(e.target.value) || 5 }))}
                    />
                  </div>
                  <div className="afm-form-group">
                    <label>Clicks per IP per Day</label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={fraudSettingsForm.clicksPerDayLimit}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, clicksPerDayLimit: parseInt(e.target.value) || 15 }))}
                    />
                  </div>
                  <div className="afm-form-group">
                    <label>Min Time Between Clicks (ms)</label>
                    <input
                      type="number"
                      min="100"
                      max="10000"
                      step="100"
                      value={fraudSettingsForm.minTimeBetweenClicksMs}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, minTimeBetweenClicksMs: parseInt(e.target.value) || 1000 }))}
                    />
                  </div>
                </div>
              </div>

              <div className="afm-form-section">
                <h4>Bot Filtering</h4>
                <div className="afm-form-grid">
                  <div className="afm-form-group">
                    <label>Bot Filter Level</label>
                    <select
                      value={fraudSettingsForm.botFilterLevel}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, botFilterLevel: e.target.value }))}
                    >
                      <option value="RELAXED">Relaxed - Basic bots only</option>
                      <option value="MODERATE">Moderate - Most common bots</option>
                      <option value="STRICT">Strict - All known bot patterns</option>
                    </select>
                  </div>
                  <div className="afm-form-group">
                    <label className="afm-toggle-label">
                      <input
                        type="checkbox"
                        checked={fraudSettingsForm.blockSuspiciousUA}
                        onChange={e => setFraudSettingsForm(prev => ({ ...prev, blockSuspiciousUA: e.target.checked }))}
                      />
                      <span>Block Suspicious User Agents</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="afm-form-section">
                <h4>Auto-Block Settings</h4>
                <div className="afm-form-grid">
                  <div className="afm-form-group">
                    <label className="afm-toggle-label">
                      <input
                        type="checkbox"
                        checked={fraudSettingsForm.autoBlockEnabled}
                        onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoBlockEnabled: e.target.checked }))}
                      />
                      <span>Auto-Block Fraudulent IPs</span>
                    </label>
                  </div>
                  <div className="afm-form-group">
                    <label>Auto-Block Threshold (clicks)</label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={fraudSettingsForm.autoBlockThreshold}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoBlockThreshold: parseInt(e.target.value) || 20 }))}
                    />
                  </div>
                </div>
              </div>

              <div className="afm-form-section">
                <h4>Auto-Flag Merchants</h4>
                <div className="afm-form-grid">
                  <div className="afm-form-group">
                    <label className="afm-toggle-label">
                      <input
                        type="checkbox"
                        checked={fraudSettingsForm.autoFlagEnabled}
                        onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoFlagEnabled: e.target.checked }))}
                      />
                      <span>Auto-Flag Merchants with Fraud</span>
                    </label>
                  </div>
                  <div className="afm-form-group">
                    <label>Auto-Flag Threshold (fraudulent clicks)</label>
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      value={fraudSettingsForm.autoFlagThreshold}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoFlagThreshold: parseInt(e.target.value) || 100 }))}
                    />
                  </div>
                </div>
              </div>

              <div className="afm-form-section">
                <h4>Auto-Suspend Merchants</h4>
                <div className="afm-form-grid">
                  <div className="afm-form-group">
                    <label className="afm-toggle-label">
                      <input
                        type="checkbox"
                        checked={fraudSettingsForm.autoSuspendEnabled}
                        onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoSuspendEnabled: e.target.checked }))}
                      />
                      <span>Auto-Suspend Repeat Offenders</span>
                    </label>
                  </div>
                  <div className="afm-form-group">
                    <label>Auto-Suspend Threshold (fraudulent clicks)</label>
                    <input
                      type="number"
                      min="50"
                      max="5000"
                      value={fraudSettingsForm.autoSuspendThreshold}
                      onChange={e => setFraudSettingsForm(prev => ({ ...prev, autoSuspendThreshold: parseInt(e.target.value) || 500 }))}
                    />
                  </div>
                </div>
              </div>

              <div className="afm-form-actions">
                <button className="afm-btn-secondary" onClick={handleCancelEditFraudSettings} disabled={savingFraudSettings}>
                  Cancel
                </button>
                <button className="afm-btn-primary" onClick={handleSaveFraudSettings} disabled={savingFraudSettings}>
                  {savingFraudSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          ) : (
            <div className="afm-overview-grid">
              <div className="afm-overview-card">
                <div className="afm-overview-header">
                  <h3>Rate Limits</h3>
                </div>
                <div className="afm-overview-body">
                  <div className="afm-perf-grid">
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Clicks per IP per Hour</span>
                      <span className="afm-perf-value">{fraudSettings.clicksPerHourLimit}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Clicks per IP per Day</span>
                      <span className="afm-perf-value">{fraudSettings.clicksPerDayLimit}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Min Time Between Clicks</span>
                      <span className="afm-perf-value">{fraudSettings.minTimeBetweenClicksMs}ms</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Auto-Block Threshold</span>
                      <span className="afm-perf-value">{fraudSettings.autoBlockThreshold} clicks</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="afm-overview-card">
                <div className="afm-overview-header">
                  <h3>Bot Filtering</h3>
                </div>
                <div className="afm-overview-body">
                  <div className="afm-perf-grid">
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Filter Level</span>
                      <span className="afm-perf-value">{fraudSettings.botFilterLevel}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Block Suspicious UA</span>
                      <span className="afm-perf-value">{fraudSettings.blockSuspiciousUA ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Auto-Block Enabled</span>
                      <span className="afm-perf-value">{fraudSettings.autoBlockEnabled ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Protection Status</span>
                      <span className="afm-perf-value">{fraudSettings.isEnabled ? 'Active' : 'Disabled'}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="afm-overview-card">
                <div className="afm-overview-header">
                  <h3>Merchant Actions</h3>
                </div>
                <div className="afm-overview-body">
                  <div className="afm-perf-grid">
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Auto-Flag Merchants</span>
                      <span className="afm-perf-value">{fraudSettings.autoFlagEnabled ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Flag Threshold</span>
                      <span className="afm-perf-value">{fraudSettings.autoFlagThreshold} clicks</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Auto-Suspend</span>
                      <span className="afm-perf-value">{fraudSettings.autoSuspendEnabled ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="afm-perf-item">
                      <span className="afm-perf-label">Suspend Threshold</span>
                      <span className="afm-perf-value">{fraudSettings.autoSuspendThreshold} clicks</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          <p>Loading fraud settings...</p>
        )}
      </div>

      {/* Merchants with Fraud Activity */}
      <div className="afm-card">
        <h3>Merchants with Suspicious Activity</h3>
        {fraudMerchants.length === 0 ? (
          <div className="afm-empty small">
            <CheckCircle size={40} />
            <h3>No Suspicious Merchants</h3>
            <p>No merchants flagged for fraudulent activity</p>
          </div>
        ) : (
          <div className="afm-table-container">
            <table className="afm-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Store</th>
                  <th>Fraudulent Clicks</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {fraudMerchants.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="afm-cell-main">{item.merchant?.email || 'Unknown'}</div>
                      <div className="afm-cell-sub">{item.merchant?.username}</div>
                    </td>
                    <td>{item.merchant?.store?.name || 'N/A'}</td>
                    <td>
                      <span className={`afm-badge ${item.fraudulentClicks > 50 ? 'status-expired' : item.fraudulentClicks > 20 ? 'status-paused' : 'status-draft'}`}>
                        {formatNumber(item.fraudulentClicks)}
                      </span>
                    </td>
                    <td>
                      {item.merchant?.adFeatureSuspended ? (
                        <span className="afm-badge status-expired">Suspended</span>
                      ) : item.merchant?.adFraudFlagged ? (
                        <span className="afm-badge status-paused">Flagged</span>
                      ) : (
                        <span className="afm-badge status-active">Active</span>
                      )}
                    </td>
                    <td>
                      <div className="afm-action-btns">
                        {item.merchant?.adFeatureSuspended ? (
                          /* Suspended - can only lift suspension */
                          <button
                            className="afm-icon-btn success"
                            onClick={() => handleLiftSuspension(item.merchant.id)}
                            disabled={actionLoading === `lift-${item.merchant.id}`}
                            title="Lift Suspension"
                          >
                            <CheckCircle size={14} />
                          </button>
                        ) : item.merchant?.adFraudFlagged ? (
                          /* Flagged - can unflag or escalate to suspend */
                          <>
                            <button
                              className="afm-icon-btn success"
                              onClick={() => handleFlagMerchant(item.merchant.id, 'unflag')}
                              disabled={actionLoading === item.merchant.id}
                              title="Unflag Merchant"
                            >
                              <CheckCircle size={14} />
                            </button>
                            <button
                              className="afm-icon-btn danger"
                              onClick={() => handleSuspendMerchant(item.merchant.id, 'Escalated to suspension due to continued violations')}
                              disabled={actionLoading === `suspend-${item.merchant.id}`}
                              title="Escalate to Suspension"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        ) : (
                          /* Active - can flag or directly suspend */
                          <>
                            <button
                              className="afm-icon-btn"
                              onClick={() => handleFlagMerchant(item.merchant.id, 'flag', 'Flagged for suspicious ad activity')}
                              disabled={actionLoading === item.merchant.id}
                              title="Flag Merchant"
                            >
                              <AlertCircle size={14} />
                            </button>
                            <button
                              className="afm-icon-btn danger"
                              onClick={() => handleSuspendMerchant(item.merchant.id, 'Suspended due to fraudulent ad activity')}
                              disabled={actionLoading === `suspend-${item.merchant.id}`}
                              title="Suspend Ad Access"
                            >
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Appeals */}
      <div className="afm-card">
        <div className="afm-card-header-flex">
          <h3>Merchant Appeals {appealCounts.PENDING > 0 && <span className="afm-badge status-paused">{appealCounts.PENDING} pending</span>}</h3>
          <div className="afm-filter-tabs">
            <button
              className={`afm-filter-tab ${appealFilter === 'PENDING' ? 'active' : ''}`}
              onClick={() => { setAppealFilter('PENDING'); fetchAppeals('PENDING'); }}
            >
              Pending ({appealCounts.PENDING})
            </button>
            <button
              className={`afm-filter-tab ${appealFilter === 'APPROVED' ? 'active' : ''}`}
              onClick={() => { setAppealFilter('APPROVED'); fetchAppeals('APPROVED'); }}
            >
              Approved ({appealCounts.APPROVED})
            </button>
            <button
              className={`afm-filter-tab ${appealFilter === 'REJECTED' ? 'active' : ''}`}
              onClick={() => { setAppealFilter('REJECTED'); fetchAppeals('REJECTED'); }}
            >
              Rejected ({appealCounts.REJECTED})
            </button>
          </div>
        </div>

        {appeals.length === 0 ? (
          <div className="afm-empty small">
            <CheckCircle size={40} />
            <h3>No {appealFilter.toLowerCase()} Appeals</h3>
            <p>{appealFilter === 'PENDING' ? 'No appeals awaiting review' : `No ${appealFilter.toLowerCase()} appeals found`}</p>
          </div>
        ) : (
          <div className="afm-table-container">
            <table className="afm-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Appeal Reason</th>
                  <th>Original Flag</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  {appealFilter === 'PENDING' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {appeals.map(appeal => (
                  <tr key={appeal.id}>
                    <td>
                      <div className="afm-cell-main">{appeal.merchant?.email || 'Unknown'}</div>
                      <div className="afm-cell-sub">{appeal.merchant?.store?.name || 'No store'}</div>
                    </td>
                    <td>
                      <div className="afm-cell-main">{appeal.reason?.replace(/_/g, ' ')}</div>
                      {appeal.details && <div className="afm-cell-sub">{appeal.details.substring(0, 50)}...</div>}
                    </td>
                    <td>
                      <div className="afm-cell-sub">{appeal.originalFlagReason || 'N/A'}</div>
                    </td>
                    <td>{new Date(appeal.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span className={`afm-badge ${appeal.status === 'PENDING' ? 'status-paused' : appeal.status === 'APPROVED' ? 'status-active' : 'status-expired'}`}>
                        {appeal.status}
                      </span>
                    </td>
                    {appealFilter === 'PENDING' && (
                      <td>
                        <div className="afm-action-btns">
                          <button
                            className="afm-icon-btn success"
                            onClick={() => handleApproveAppeal(appeal.id)}
                            disabled={processingAppeal === appeal.id}
                            title="Approve Appeal"
                          >
                            <CheckCircle size={14} />
                          </button>
                          <button
                            className="afm-icon-btn danger"
                            onClick={() => handleRejectAppeal(appeal.id)}
                            disabled={processingAppeal === appeal.id}
                            title="Reject Appeal"
                          >
                            <XCircle size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alerts */}
      <div className="afm-card">
        <h3>Active Alerts</h3>
        {fraudAlerts.length === 0 ? (
          <div className="afm-empty small">
            <Shield size={40} />
            <h3>No Active Alerts</h3>
            <p>The system is monitoring for suspicious activity</p>
          </div>
        ) : (
          <div className="afm-alerts-list">
            {fraudAlerts.map(alert => (
              <div key={alert.id} className={`afm-alert-card severity-${alert.severity?.toLowerCase() || 'medium'}`}>
                <div className="afm-alert-header">
                  <div className="afm-alert-type">
                    <AlertCircle size={16} />
                    <span>{alert.alertType?.replace(/_/g, ' ') || 'Suspicious Activity'}</span>
                  </div>
                  <span className={`afm-severity-badge ${alert.severity?.toLowerCase()}`}>{alert.severity || 'MEDIUM'}</span>
                </div>
                <div className="afm-alert-body">
                  <div><strong>Campaign:</strong> {alert.campaignName || alert.campaignId}</div>
                  <div><strong>Merchant:</strong> {alert.merchantEmail || 'Unknown'}</div>
                  <div><strong>IP Hash:</strong> {alert.ipHash?.substring(0, 12)}...</div>
                  <div><strong>Clicks:</strong> {alert.clickCount}</div>
                </div>
                <button className="afm-btn afm-btn-outline small" onClick={() => handleDismissFraudAlert(alert.id)} disabled={actionLoading === alert.id}>
                  {actionLoading === alert.id ? 'Dismissing...' : 'Dismiss'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const renderScheduler = () => (
    <>
      <div className="afm-section-header">
        <h2>Campaign Scheduler</h2>
        <div className="afm-header-actions">
          <button className="afm-btn afm-btn-primary" onClick={handleRunScheduler} disabled={actionLoading === 'scheduler'}>
            <Play size={14} /> {actionLoading === 'scheduler' ? 'Running...' : 'Run Now'}
          </button>
          <button className="afm-btn afm-btn-outline" onClick={fetchSchedulerStatus}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="afm-overview-grid">
        <div className="afm-scheduler-card">
          <div className="afm-scheduler-icon"><Clock size={28} /></div>
          <div className="afm-scheduler-content">
            <h3>Scheduler Status</h3>
            <div className="afm-status-indicator">
              <span className="afm-status-dot active"></span>
              Active - Runs every 5 minutes
            </div>
            {schedulerStatus?.lastRun && (
              <p className="afm-last-run">Last run: {new Date(schedulerStatus.lastRun).toLocaleString()}</p>
            )}
          </div>
        </div>

        <div className="afm-scheduler-card">
          <h4>What the scheduler does:</h4>
          <ul className="afm-scheduler-list">
            <li><Play size={14} /> <strong>Activates SCHEDULED campaigns</strong> when start time is reached</li>
            <li><Pause size={14} /> <strong>Expires ACTIVE campaigns</strong> when end time passes</li>
            <li><Calendar size={14} /> <strong>Expires subscriptions</strong> when end date passes</li>
          </ul>
        </div>
      </div>

      {/* Stats */}
      {schedulerStatus && (
        <div className="afm-summary-grid three-col">
          <div className="afm-summary-card">
            <span className="afm-summary-label">Pending Activations</span>
            <span className="afm-summary-value">{schedulerStatus.pendingActivations || 0}</span>
          </div>
          <div className="afm-summary-card">
            <span className="afm-summary-label">Pending Expirations</span>
            <span className="afm-summary-value">{schedulerStatus.pendingExpirations || 0}</span>
          </div>
          <div className="afm-summary-card">
            <span className="afm-summary-label">Expiring Subscriptions</span>
            <span className="afm-summary-value">{schedulerStatus.expiringSubscriptions || 0}</span>
          </div>
        </div>
      )}
    </>
  );

  // =========================================
  // MAIN RENDER
  // =========================================

  if (loading && activeTab === 'overview') {
    return (
      <div className="afm-container">
        <div className="afm-loading">
          <div className="afm-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="afm-container">
      {/* Toast */}
      {toast && (
        <div className={`afm-toast ${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="afm-page-header">
        <div className="afm-page-title">
          <Megaphone size={28} />
          <h1>Storefront Ads</h1>
        </div>
        <button className="afm-btn afm-btn-outline" onClick={fetchData} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'afm-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="afm-tabs">
        <button className={`afm-tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
        <button className={`afm-tab ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')}>Subscriptions</button>
        <button className={`afm-tab ${activeTab === 'campaigns' ? 'active' : ''}`} onClick={() => setActiveTab('campaigns')}>Campaigns</button>
        <button className={`afm-tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>Analytics</button>
        <button className={`afm-tab ${activeTab === 'fraud' ? 'active' : ''}`} onClick={() => setActiveTab('fraud')}>
          <Shield size={14} /> Fraud
        </button>
        <button className={`afm-tab ${activeTab === 'scheduler' ? 'active' : ''}`} onClick={() => setActiveTab('scheduler')}>
          <Clock size={14} /> Scheduler
        </button>
      </div>

      {/* Content */}
      <div className="afm-content">
        {loading ? (
          <div className="afm-loading">
            <div className="afm-spinner"></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'subscriptions' && renderSubscriptions()}
            {activeTab === 'campaigns' && renderCampaigns()}
            {activeTab === 'analytics' && renderAnalytics()}
            {activeTab === 'fraud' && renderFraud()}
            {activeTab === 'scheduler' && renderScheduler()}
          </>
        )}
      </div>

      {/* Config Modal */}
      {configModal && (
        <div className="afm-modal-overlay" onClick={() => setConfigModal(false)}>
          <div className="afm-modal" onClick={e => e.stopPropagation()}>
            <div className="afm-modal-header">
              <h3>Configure Ad Feature</h3>
              <button className="afm-modal-close" onClick={() => setConfigModal(false)}><X size={18} /></button>
            </div>
            <div className="afm-modal-body">
              <div className="afm-form-group">
                <label className="afm-toggle-label">
                  <input type="checkbox" checked={configForm.isEnabled} onChange={e => setConfigForm(p => ({ ...p, isEnabled: e.target.checked }))} />
                  <span>Enable Feature</span>
                </label>
              </div>
              <div className="afm-form-group">
                <label>Monthly Price (NGN)</label>
                <input type="number" value={configForm.monthlyPriceNGN} onChange={e => setConfigForm(p => ({ ...p, monthlyPriceNGN: Number(e.target.value) }))} />
              </div>
              <div className="afm-form-group">
                <label>Yearly Price (NGN)</label>
                <input type="number" value={configForm.yearlyPriceNGN} onChange={e => setConfigForm(p => ({ ...p, yearlyPriceNGN: Number(e.target.value) }))} />
              </div>
              <div className="afm-form-group">
                <label>Yearly Discount (%)</label>
                <input type="number" value={configForm.yearlyDiscount} onChange={e => setConfigForm(p => ({ ...p, yearlyDiscount: Number(e.target.value) }))} />
              </div>
            </div>
            <div className="afm-modal-footer">
              <button className="afm-btn afm-btn-outline" onClick={() => setConfigModal(false)}>Cancel</button>
              <button className="afm-btn afm-btn-primary" onClick={handleSaveConfig} disabled={actionLoading === 'config'}>
                {actionLoading === 'config' ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grant Modal */}
      {grantModal && (
        <div className="afm-modal-overlay" onClick={() => setGrantModal(false)}>
          <div className="afm-modal" onClick={e => e.stopPropagation()}>
            <div className="afm-modal-header">
              <h3>Grant Subscription</h3>
              <button className="afm-modal-close" onClick={() => setGrantModal(false)}><X size={18} /></button>
            </div>
            <div className="afm-modal-body">
              <div className="afm-form-group">
                <label>Search Merchant</label>
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={merchantSearch}
                  onChange={e => { setMerchantSearch(e.target.value); searchMerchants(e.target.value); }}
                />
                {merchantResults.length > 0 && (
                  <div className="afm-merchant-results">
                    {merchantResults.map(m => (
                      <div
                        key={m.id}
                        className={`afm-merchant-result ${grantForm.merchantId === m.id ? 'selected' : ''}`}
                        onClick={() => { setGrantForm(p => ({ ...p, merchantId: m.id })); setMerchantSearch(m.email); setMerchantResults([]); }}
                      >
                        <span>{m.email}</span>
                        <span className="afm-merchant-store">{m.store?.name || 'No store'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="afm-form-group">
                <label>Plan</label>
                <select value={grantForm.plan} onChange={e => setGrantForm(p => ({ ...p, plan: e.target.value }))}>
                  <option value="MONTHLY">Monthly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
              <div className="afm-form-group">
                <label>Duration (months)</label>
                <input type="number" min="1" value={grantForm.durationMonths} onChange={e => setGrantForm(p => ({ ...p, durationMonths: Number(e.target.value) }))} />
              </div>
            </div>
            <div className="afm-modal-footer">
              <button className="afm-btn afm-btn-outline" onClick={() => setGrantModal(false)}>Cancel</button>
              <button className="afm-btn afm-btn-primary" onClick={handleGrantSubscription} disabled={actionLoading === 'grant'}>
                {actionLoading === 'grant' ? 'Granting...' : 'Grant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal.isOpen && (
        <div className="afm-modal-overlay" onClick={() => setConfirmModal({ isOpen: false, action: null, data: null })}>
          <div className="afm-modal small" onClick={e => e.stopPropagation()}>
            <div className="afm-modal-header">
              <h3>Confirm Action</h3>
              <button className="afm-modal-close" onClick={() => setConfirmModal({ isOpen: false, action: null, data: null })}><X size={18} /></button>
            </div>
            <div className="afm-modal-body">
              {confirmModal.action === 'delete' && <p>Delete campaign "<strong>{confirmModal.data?.headline || 'Untitled'}</strong>"? This cannot be undone.</p>}
              {confirmModal.action === 'pause' && <p>Pause campaign "<strong>{confirmModal.data?.headline || 'Untitled'}</strong>"?</p>}
              {confirmModal.action === 'bulk-pause' && <p>Pause <strong>{selectedCampaigns.length}</strong> selected campaigns?</p>}
              {confirmModal.action === 'bulk-resume' && <p>Resume <strong>{selectedCampaigns.length}</strong> selected campaigns?</p>}
              {confirmModal.action === 'bulk-delete' && <p>Delete <strong>{selectedCampaigns.length}</strong> selected campaigns? This cannot be undone.</p>}
            </div>
            <div className="afm-modal-footer">
              <button className="afm-btn afm-btn-outline" onClick={() => setConfirmModal({ isOpen: false, action: null, data: null })}>Cancel</button>
              <button
                className={`afm-btn ${confirmModal.action?.includes('delete') ? 'afm-btn-danger' : 'afm-btn-primary'}`}
                onClick={() => {
                  if (confirmModal.action === 'delete') handleCampaignAction(confirmModal.data.id, 'delete');
                  else if (confirmModal.action === 'pause') handleCampaignAction(confirmModal.data.id, 'pause');
                  else if (confirmModal.action?.startsWith('bulk-')) handleBulkAction(confirmModal.action.replace('bulk-', ''));
                }}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdFeatureManagement;
